import { _ } from "../../core/polyglot/polyglot.js";
import {
	addMonths,
	daysBetween,
	newDate,
	normalizeDate,
	toNextDay,
} from "../../core/helpers/date-util.js";
import {
	formatAsDashedYearMonthDay,
	formatAsShortDate,
	formatAsYear,
} from "../../core/helpers/date-formatting-util.js";
import { isNotNull, isNull } from "../../core/helpers/object-util.js";
import {
	lastElementsOfArray,
	padArray,
} from "@foretagsplatsen/core-helpers/array";
import Account from "../Account.js";
import Bimester from "./Bimester.js";
import buildSubPeriodsBetween from "../../core/buildSubPeriodsBetween.js";
import ConsecutiveMonths from "./ConsecutiveMonths.js";
import Day from "./Day.js";
import Dimension from "../Dimension.js";
import HalfYear from "./HalfYear.js";
import Month from "./Month.js";
import nullPeriod from "./nullPeriod.js";
import Period from "./Period.js";
import Quarter from "./Quarter.js";
import Timespan from "./Timespan.js";
import Week from "./Week.js";

/**
 * `fiscalYear` is a period repesenting a fiscal year.
 *
 * It contains all the sub periods of the year: days, weeks,
 * months, quarters and half years, built from the `spec`
 * options.
 *
 * @param {string} spec.id - Unique identifier
 * @param {string} spec.currencyCultureName - Currency to use to format values
 * @param {string} spec.chartOfAccountsId - Identifier of the chart of accounts used to compute this fiscal year
 * @param {string} spec.name - name of the fiscal year
 * @param {object[]} [spec.months] - list of months composing the fiscal year
 * @param {object[]} [spec.days] - list of days composing the fiscal year
 * @param {object[]} [spec.weeks] - list of weeks composing the fiscal year
 * @param {object[]} [spec.quarters] - list of quarters composing the fiscal year
 * @param {object[]} [spec.bimesters] - list of bimesters composing the fiscal year
 * @param {object[]} [spec.halfYears] - list of semesters composing the fiscal year
 * @param {Date} spec.lastTransactionDate - Date of the last transaction within the fiscal year
 * @param {Date} spec.lastActiveTransactionDate - Date of the last transaction to consider within the fiscal year
 * @param {dimension} [spec.dimensionTree] - Tree of dimensions for the fiscal year
 * @param {budget[]} [spec.budgets] - List of budgets for the fiscal year
 * @param {account[]} [spec.accounts] - List of accounts for the fiscal year
 * @param {boolean} [spec.hasDimensionOpeningBalances=false] - True if fiscal year got imported after dimension opening balance introduction
 */
export default class FiscalYear extends Period {
	constructor(spec = {}) {
		super(spec);

		this._periodIndex = 1;
		this._id = spec.id || "";
		this._year = this;
		this._name = spec.name;
		this._periodId = this._name;

		this._currencyCultureName = spec.currencyCultureName;
		this._chartOfAccountsId = spec.chartOfAccountsId;
		this._lastTransactionDate = spec.lastTransactionDate;
		this._lastActiveTransactionDate = spec.lastActiveTransactionDate;

		this._dimensionTree =
			spec.dimensionTree ||
			new Dimension({
				name: "root",
			});

		this._budgets = spec.budgets || [];

		this._months = spec.months || [];
		this._bimesters = spec.bimesters || [];
		this._weeks = spec.weeks || [];
		this._quarters = spec.quarters || [];
		this._halfYears = spec.halfYears || [];
		this._days = spec.days || [];

		this._accounts = spec.accounts || [];

		this._getAllPeriods().forEach((period) => {
			period.setYear(this);
		});

		this._hasDimensionOpeningBalances = !!spec.hasDimensionOpeningBalances;
	}

	getAccounts() {
		return this._accounts;
	}

	setAccounts(newAccounts) {
		this._accounts = newAccounts;
	}

	findAccount(accountNumber) {
		return this._accounts.find(
			(account) => account.getNumber() === accountNumber,
		);
	}

	getId() {
		return this._id;
	}

	getDimensionTree() {
		return this._dimensionTree;
	}

	setDimensionTree(dimensionTree) {
		this._dimensionTree = dimensionTree;
	}

	getDimensionId() {
		return this._dimensionTree && this._dimensionTree.getId();
	}

	getBudgets() {
		return this._budgets.slice();
	}

	setBudgets(budgets) {
		if (isNull(budgets)) {
			return;
		}
		this._budgets = budgets.slice();
	}

	addBudget(budget) {
		this._budgets.push(budget);
	}

	removeBudget(budget) {
		this._budgets = this._budgets.filter((b) => b !== budget);
	}

	getCurrencyCultureName() {
		return this._currencyCultureName;
	}

	setCurrencyCultureName(newCurrencyCultureName) {
		this._currencyCultureName = newCurrencyCultureName;
	}

	getChartOfAccountsId() {
		return this._chartOfAccountsId;
	}

	setChartOfAccountsId(newChartOfAccountsId) {
		this._chartOfAccountsId = newChartOfAccountsId;
	}

	getLastActiveTransactionDate() {
		return this._lastActiveTransactionDate;
	}

	setLastActiveTransactionDate(newLastActiveTransactionDate) {
		this._lastActiveTransactionDate = newLastActiveTransactionDate;
	}

	getCurrentBudget() {
		let budget;
		for (let i = 0; i < this._budgets.length; i++) {
			budget = this._budgets[i];
			if (budget.isCurrent()) {
				return budget;
			}
		}
		return null;
	}

	forecastFollowingYear() {
		const numberOfMonths = this.durationAsMonths();
		const start = toNextDay(this.getEnd());

		const end = addMonths(start, numberOfMonths);

		const periodsData = buildSubPeriodsBetween(start, end, {
			includeBimesters: !!this._bimesters.length,
		});

		this.constructor._transformSubPeriodsData(periodsData);

		return new this.constructor({
			start,
			end,
			name: this.constructor._buildName(start, end),
			accounts: this._accounts,
			chartOfAccountsId: this._chartOfAccountsId,
			currencyCultureName: this._currencyCultureName,
			dimensionTree: this._dimensionTree,
			hasDimensionOpeningBalances: this._hasDimensionOpeningBalances,
			...periodsData,
		});
	}

	includesMonthRangeByIndexes({
		startYear,
		startMonthIndex,
		endYear,
		endMonthIndex,
	}) {
		const startMonthDate = new Date(startYear, startMonthIndex, 1);
		const endMonthDate = new Date(endYear, endMonthIndex + 1, 0);

		return (
			startMonthDate >= this.getStart() && endMonthDate <= this.getEnd()
		);
	}

	setCurrentBudget(budget) {
		// Since the budgets hold the state to know if they
		// are current or not, we first need to "unset" the
		// current budget if any. (the fiscalYear could
		// instead have this state).
		const previousCurrent = this.getCurrentBudget();
		if (previousCurrent) {
			previousCurrent.setCurrent(false);
		}
		budget.setCurrent(true);
	}

	getGroupLabel() {
		return _("Year");
	}

	hasDimensionOpeningBalances() {
		return this._hasDimensionOpeningBalances;
	}

	numberOfDaysToLastActiveTransaction() {
		return daysBetween(this._lastActiveTransactionDate, this._start);
	}

	numberOfDaysToLastTransaction() {
		return daysBetween(this._lastTransactionDate, this._start);
	}

	getLastTransactionDate() {
		if (!this._lastTransactionDate) {
			return this.getStart();
		}

		return this._lastTransactionDate;
	}

	/**
	 * Returns the number of percent of the fiscal year this has passed.
	 *
	 * @return {number} A number between 0 ... 100
	 */
	getActiveProgress() {
		if (!this.isActive()) {
			return 0;
		}
		return Math.floor(
			((this.numberOfDaysToLastActiveTransaction() + 1) /
				this.numberOfDays()) *
				100,
		);
	}

	getProgress() {
		return Math.floor(
			((this.numberOfDaysToLastTransaction() + 1) / this.numberOfDays()) *
				100,
		);
	}

	getInactiveProgress() {
		return this.getProgress() - this.getActiveProgress();
	}

	asDays() {
		return this._days.slice();
	}

	asWeeks() {
		return this._weeks.slice();
	}

	asMonths() {
		return this._months.slice();
	}

	asBimesters() {
		return this._bimesters.slice();
	}

	asQuarters() {
		return this._quarters.slice();
	}

	asHalfYears() {
		return this._halfYears.slice();
	}

	asYears() {
		return [this];
	}

	asFiscalYearReference() {
		return {
			start: this.getStart(),
			end: this.getEnd(),
			fiscalYearId: this.getId(),
		};
	}

	asMonthsUntilLastTransactionDate() {
		const fiscalYearUntilLastTransactionDate = new Timespan({
			start: this.getStart(),
			end: this._lastTransactionDate,
		});

		return this._months.filter((month) =>
			fiscalYearUntilLastTransactionDate.includesAnyOf(month),
		);
	}

	asMonthsWithinActivePeriod() {
		return this._months.filter(this.isWithinActivePeriod.bind(this));
	}

	asBimestersWithinActivePeriod() {
		return this._bimesters.filter(this.isWithinActivePeriod.bind(this));
	}

	asQuartersWithinActivePeriod() {
		return this._quarters.filter(this.isWithinActivePeriod.bind(this));
	}

	asHalfYearsWithinActivePeriod() {
		return this._halfYears.filter(this.isWithinActivePeriod.bind(this));
	}

	asPeriods(periodType) {
		if (periodType === "Year") {
			return this.asYears();
		}
		if (periodType === "HalfYear") {
			return this.asHalfYears();
		}
		if (periodType === "Quarter") {
			return this.asQuarters();
		}
		if (periodType === "Month") {
			return this.asMonths();
		}
		if (periodType === "Bimester") {
			return this.asBimesters();
		}
		if (periodType === "Week") {
			return this.asWeeks();
		}
		if (periodType === "Day") {
			return this.asDays();
		}
		return null;
	}

	/**
	 * Convert the fiscal year to a consecutive month, including
	 * only the last active months using a specific length.
	 *
	 * @param {Integer} [length] - Size of the ConsecutiveMonths starting
	 * from the end of the fiscal year's active months.
	 */
	asLastConsecutiveMonthsWithinActivePeriod({ length = 2 } = {}) {
		const allActiveMonths = this.asMonthsWithinActivePeriod();
		length = Math.min(length, allActiveMonths.length);

		const periodIndex =
			allActiveMonths[allActiveMonths.length - length].getPeriodIndex();

		return new ConsecutiveMonths({
			year: this,
			periodIndex,
			additionalMonths: length - 1,
		});
	}

	getAllPeriods() {
		return this._getAllPeriods();
	}

	getAllActivePeriods() {
		return this._months
			.concat(this._bimesters)
			.concat(this._quarters)
			.concat(this._halfYears)
			.concat([this])
			.filter(this.isWithinActivePeriod.bind(this));
	}

	getLastMonthIndex() {
		const months = this.asMonths();
		const lastMonth = months.at(-1);
		return lastMonth.getEnd().getMonth();
	}

	getLastActiveMonth() {
		return this.getLastActivePeriod("Month");
	}

	getLastActivePeriod(periodType) {
		const activePeriods = this.asPeriods(periodType).filter(
			this.isWithinActivePeriod.bind(this),
		);
		return activePeriods.at(-1);
	}

	lastActiveMonth() {
		if (!this.isActive()) {
			return nullPeriod;
		}
		return this._months.find((month) =>
			month.includes(this.getLastActiveTransactionDate()),
		);
	}

	lastMonthWithTransaction() {
		return this._months.find((month) =>
			month.includes(this.getLastTransactionDate()),
		);
	}

	findSameCalendarPeriods(otherPeriod) {
		return this.asPeriods(otherPeriod.getPeriodType()).filter((period) =>
			this._isSameWithoutYear(period, otherPeriod),
		);
	}

	findPeriod(periodType, periodIndex) {
		return this.asPeriods(periodType)[periodIndex - 1];
	}

	findPeriodById(periodString) {
		return periodString.includes("ConsecutiveMonths")
			? this._buildConsecutiveMonthsPeriod(periodString)
			: this.getAllPeriods().find(
					(period) => period.toString() === periodString,
				) || nullPeriod;
	}

	findActivePeriod(periodType, periodIndex) {
		const period = this.findPeriod(periodType, periodIndex);
		if (period && period.isActive()) {
			return period;
		}
		return null;
	}

	findEquivalentActivePeriod(timespan) {
		if (timespan.isConsecutiveMonths?.()) {
			return new ConsecutiveMonths({
				additionalMonths: timespan.getAdditionalMonths(),
				label: timespan.getLabel(),
				name: timespan.getName(),
				periodIndex: timespan.getPeriodIndex(),
				year: this,
			});
		}

		return this._year
			.getAllActivePeriods()
			.find((period) => period.equates(timespan));
	}

	getActivePeriod() {
		if (!this.isActive()) {
			return nullPeriod;
		}
		return new Timespan({
			start: this.getStart(),
			end: this.getLastActiveTransactionDate(),
		});
	}

	isWithinActivePeriod(period) {
		return this.getActivePeriod().includesAnyOf(period);
	}

	isBroken() {
		return this._start.getFullYear() !== this._end.getFullYear();
	}

	isActive() {
		return isNotNull(this.getLastActiveTransactionDate());
	}

	isYear() {
		return true;
	}

	toSmartString() {
		return (
			this._name || this.constructor._buildName(this._start, this._end)
		);
	}

	hasInactiveTransactions() {
		return this._lastTransactionDate > this._lastActiveTransactionDate;
	}

	hasSameMonthsRange(fiscalYear) {
		return this.asMonths().length === fiscalYear.asMonths().length;
	}

	toRangeString() {
		const start = formatAsShortDate(this.getStart());
		const end = formatAsShortDate(this.getEnd());

		return _("Fiscal Year: {range}", {
			range: `${start} - ${end}`,
		});
	}

	getAccountsOfGroups({ accountGroups }) {
		return this._accounts.filter((account) =>
			accountGroups.some((accountGroup) =>
				accountGroup.hasAccount(account),
			),
		);
	}

	_serializedProperties() {
		return {
			id: this.getId(),
			currencyCultureName: this.getCurrencyCultureName(),
			chartOfAccountsId: this.getChartOfAccountsId(),
			lastActiveTransactionDate: this.isActive()
				? formatAsDashedYearMonthDay(
						this.getLastActiveTransactionDate(),
					)
				: null,
		};
	}

	/**
	 * Compare two periods on month and day basis. It will also take leap years in consideration
	 * and return true if one period start or ends with a leap day and the other doesn't.
	 */
	_isSameWithoutYear(periodA, periodB) {
		const periodAStart = normalizeDate(periodA.getStart());
		const periodAEnd = normalizeDate(periodA.getEnd());
		const periodBStart = normalizeDate(periodB.getStart());
		const periodBEnd = normalizeDate(periodB.getEnd());

		return (
			periodAStart.isSame(periodBStart, "day") &&
			periodAEnd.isSame(periodBEnd, "day")
		);
	}

	_getAllPeriods() {
		return [
			...this._days,
			...this._weeks,
			...this._months,
			...this._bimesters,
			...this._halfYears,
			...this._quarters,
			this,
		];
	}

	_buildConsecutiveMonthsPeriod(periodString) {
		const periodValues = periodString.split("-")[1].split("_");
		const periodIndex = parseInt(periodValues[0]);
		const additionalMonths = parseInt(periodValues[1]);

		return new ConsecutiveMonths({
			year: this,
			periodIndex,
			additionalMonths,
		});
	}

	static getChartOfAccounts() {
		return [
			{ name: "BAS95EN", chartOfAccountsId: "BAS95EN" },
			{ name: "BAS96", chartOfAccountsId: "BAS96" },
			{ name: "BAS96EN", chartOfAccountsId: "BAS96EN" },
			{ name: "EUBAS97", chartOfAccountsId: "EUBAS97" },
			{ name: "EUBAS97EN", chartOfAccountsId: "EUBAS97EN" },
			{ name: "EUBAS97NO", chartOfAccountsId: "EUBAS97NO" },
			{ name: "EUBAS97FI", chartOfAccountsId: "EUBAS97FI" },
			{ name: "EUBAS97DK", chartOfAccountsId: "EUBAS97DK" },
			{ name: "FAST2005", chartOfAccountsId: "FAST2005" },
			{ name: "NS4102", chartOfAccountsId: "NS4102" },
			{
				name: "Aktiebolag BAS2011",
				chartOfAccountsId: "Aktiebolag BAS2011",
			},
			{ name: "BRF", chartOfAccountsId: "BRF" },
			{ name: "BAS2015ICA", chartOfAccountsId: "BAS2015ICA" },
			{ name: "DK standard blank", chartOfAccountsId: "WKDenmarkBase" },
			{ name: "DK Uniconta", chartOfAccountsId: "WKDenmarkStandard" },
			{ name: "DK e-conomic", chartOfAccountsId: "ECONOMICDK" },
			{ name: "GruposNormal", chartOfAccountsId: "GRUPOSNORMAL" },
			{ name: "GruposPymes", chartOfAccountsId: "GRUPOSPYMES" },
			{ name: "GruposAbreviado", chartOfAccountsId: "GRUPOSABREVIADO" },
			{ name: "Twinfield", chartOfAccountsId: "TWINFIELD" },
		];
	}

	static transformMaterializationData(data, context = {}) {
		data.start = newDate(data.start);
		data.end = newDate(data.end);

		data.lastTransactionDate =
			typeof data.lastTransactionDate === "string"
				? newDate(data.lastTransactionDate)
				: data.lastTransactionDate;

		data.lastActiveTransactionDate =
			typeof data.lastActiveTransactionDate === "string"
				? newDate(data.lastActiveTransactionDate)
				: data.lastActiveTransactionDate;

		this._transformSubPeriodsData(data);

		context.root = true;

		data.dimensionTree = Dimension.materialize(
			data.dimensionTree || {},
			context,
		);

		data.accounts = (data.accounts || []).map(
			(datum) => new Account(datum),
		);
	}

	static getPreviousPeriods(options) {
		const years = options.years;
		const numberOfPeriods = options.numberOfPeriods;
		const lastPeriod = options.lastPeriod;

		const allPeriods = years.flatMap((year) =>
			year.asPeriods(lastPeriod.getPeriodType()),
		);

		allPeriods.sort(
			(periodA, periodB) => periodA.getStart() - periodB.getStart(),
		);

		const lastPeriodIndex = allPeriods.indexOf(lastPeriod);

		return lastElementsOfArray({
			array: allPeriods,
			numElements: numberOfPeriods,
			indexOfLastElement: lastPeriodIndex,
		});
	}

	static getLatestPeriods(options) {
		const previousPeriods = this.getPreviousPeriods(options);
		const numberOfPeriods = options.numberOfPeriods;
		return padArray(previousPeriods, numberOfPeriods, null);
	}

	static getType() {
		return "Year";
	}

	static _transformSubPeriodsData(data) {
		data.days = data.dayData
			? data.dayData.map(this._periodBuilderIterator(Day).bind(this))
			: this._buildDays(data.start);

		data.weeks = (data.weekData || []).map(
			this._periodBuilderIterator(Week).bind(this),
		);

		data.months = (data.monthData || []).map(
			this._periodBuilderIterator(Month).bind(this),
		);

		data.bimesters = (data.bimestersData || []).map(
			this._periodBuilderIterator(Bimester).bind(this),
		);

		data.quarters = (data.quarterData || []).map(
			this._periodBuilderIterator(Quarter).bind(this),
		);

		data.halfYears = (data.halfYearData || []).map(
			this._periodBuilderIterator(HalfYear).bind(this),
		);
	}

	static _buildName(start, end) {
		const startYear = formatAsYear(start);
		const endYear = formatAsYear(end);

		return startYear === endYear
			? `${startYear}`
			: `${startYear}-${endYear}`;
	}

	static _buildDays(start) {
		const days = [];
		let periodIndex = 1;
		let dayStart = new Date(start);
		const year = dayStart.getYear();
		let dayEnd;
		while (dayStart.getYear() === year) {
			dayEnd = new Date(dayStart);
			dayEnd.setHours(23, 59, 59);

			days.push(
				new Day({
					periodIndex,
					start: dayStart,
					end: dayEnd,
				}),
			);

			periodIndex++;

			dayStart = new Date(
				dayStart.getFullYear(),
				dayStart.getMonth(),
				dayStart.getDate() + 1,
			);
		}
		return days;
	}

	static _periodBuilderIterator(constructor) {
		return (data, index) => {
			data.periodIndex = index + 1;
			data.start = newDate(data.start);
			data.end = newDate(data.end);
			return new constructor(data);
		};
	}
}
